import type { User } from "@/types";

/**
 * Archive hub (`/archive`) lists cross-studio archived items with restore / delete-forever.
 * **UX-15 / UX-61 (PM DECIDED 2026-08-30):** an **Owner or Manager** of a still-existing studio
 * may open this surface. A Manager sees only conversation rows + Restore (studio rows and
 * Delete forever are gated by {@link userCanManageArchivedStudios} / `canPurgeConversationAsOwner`).
 * **Staff** stay excluded (same shell pattern as **UX-12** hiding unusable Settings tabs for Staff).
 * Ignores stale `studioRoles` rows for studios no longer in `user.studios`.
 */
export function userCanAccessArchiveHub(user: User | null): boolean {
  if (!user) return false;
  const validIds = new Set((user.studios ?? []).map((s) => s.id));
  return (user.studioRoles ?? []).some(
    (sr) => validIds.has(sr.studioId) && (sr.role === "Owner" || sr.role === "Manager"),
  );
}

/**
 * Owner-only studio-lifecycle capability inside the Archive hub — the **pre-UX-61 hub semantics**
 * (Owner of ≥1 still-existing studio). Used to keep studio archive/restore/purge and Delete forever
 * Owner-only after UX-61 opened the hub to Managers (PM-UX61-NO-STUDIO-MANAGER-1 / NO-PURGE-MANAGER-1):
 * a Manager returns `false`, so studio rows are hidden and studio actions never render for them.
 * Ignores stale `studioRoles` rows for studios no longer in `user.studios`.
 */
export function userCanManageArchivedStudios(user: User | null): boolean {
  if (!user) return false;
  const validIds = new Set((user.studios ?? []).map((s) => s.id));
  return (user.studioRoles ?? []).some((sr) => validIds.has(sr.studioId) && sr.role === "Owner");
}
